import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { MagnifyingGlass, GraduationCap, Flower, HeartStraight, Plus, Users, FunnelSimple } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { VerificationBadge, AiBadge } from "@/components/MemorialBadges";

function MemorialCard({ m }) {
  const years = [m.dob, m.dop].filter(Boolean).join(" – ");
  return (
    <Link to={`/memorials/${m.id}`} data-testid={`memorial-card-${m.id}`}
      className="group flex flex-col items-center rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
      <div className="mb-4 h-28 w-28 overflow-hidden rounded-full ring-4 ring-green-100">
        {m.photo_file_id
          ? <img src={fileUrl(m.photo_file_id)} alt={m.name} className="h-full w-full object-cover" />
          : <span className="grid h-full w-full place-items-center bg-gradient-to-br from-green-50 to-orange-50 text-2xl font-heading text-green-700">{(m.name || "?")[0]}</span>}
      </div>
      <h3 className="font-heading text-lg font-medium">{m.name}</h3>
      {years && <p className="mt-0.5 text-sm text-muted-foreground">{years}</p>}
      {(m.class_batch || m.institution) && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><GraduationCap size={13} /> {[m.class_batch, m.institution].filter(Boolean).join(" · ")}</p>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
        <VerificationBadge status={m.verification_status} />
        <AiBadge generated={m.ai_generated} assisted={m.ai_assisted} />
      </div>
      {m.epitaph && <p className="mt-3 text-sm italic text-foreground/70">“{m.epitaph}”</p>}
    </Link>
  );
}

const DECADES = ["1950", "1960", "1970", "1980", "1990", "2000", "2010", "2020"];

export default function Memorials() {
  useSeo("Obituaries & Memorials", "Remember the people who shaped our journey — classmates, teachers, alumni and friends.");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [decade, setDecade] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (decade) params.set("decade", decade);
    api.get(`/public/memorials?${params.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => {
    const t = setTimeout(load, rows === null ? 0 : 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, decade]);

  return (
    <div data-testid="memorials-page">
      <section className="mx-auto max-w-5xl px-5 pt-12 text-center">
        <div className="mx-auto mb-6 overflow-hidden rounded-[2rem] border border-border/60 shadow-sm">
          <img src="/article-covers/memorial-hero.png" alt="Forever in our hearts" className="w-full object-cover" />
        </div>
        <p className="overline text-secondary mb-2">Every institution. Every generation. Every connection.</p>
        <h1 className="font-heading text-4xl font-light tracking-tight sm:text-5xl">Obituaries &amp; Memorials</h1>
        <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
          Remember the people who shaped our journey. Preserve the lives, stories, friendships and contributions of classmates, teachers, alumni and members of educational communities around the world.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/memorials/create"><Button className="rounded-full gap-2" data-testid="create-memorial-btn"><Plus size={16} /> Create a Memorial</Button></Link>
          <Link to="/memorials/suggest"><Button variant="outline" className="rounded-full gap-2" data-testid="suggest-memorial-btn"><Users size={16} /> Suggest a Memorial</Button></Link>
        </div>

        <div className="relative mx-auto mt-8 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a memorial — name, institution or class…" className="rounded-full pl-10" data-testid="memorial-search" />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-testid="decade-filters">
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><FunnelSimple size={13} /> Browse by decade</span>
          {DECADES.map((d) => (
            <button key={d} onClick={() => setDecade(decade === d ? "" : d)} data-testid={`decade-${d}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${decade === d ? "border-green-600 bg-green-600 text-white" : "border-slate-200 text-muted-foreground hover:border-green-400"}`}>{d}s</button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-12">
        {rows === null ? <p className="text-center text-muted-foreground">Loading…</p>
          : rows.length === 0 ? (
            <div className="mx-auto max-w-xl rounded-2xl border border-dashed border-border/60 p-12 text-center text-muted-foreground" data-testid="memorials-empty">
              <Flower size={40} weight="duotone" className="mx-auto mb-3 text-secondary" />
              No memorials found. Be the first to <Link to="/memorials/create" className="text-secondary hover:underline">create a memorial</Link> or <Link to="/memorials/suggest" className="text-secondary hover:underline">suggest one</Link>.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((m) => <MemorialCard key={m.id} m={m} />)}
            </div>
          )}
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-16 text-center">
        <div className="rounded-2xl border border-secondary/20 bg-secondary/5 p-8">
          <HeartStraight size={28} weight="fill" className="mx-auto mb-3 text-secondary" />
          <p className="font-heading text-lg font-light italic text-foreground/80">Remember. Honor. Preserve.</p>
          <p className="mt-2 text-sm text-muted-foreground">Read our <Link to="/memorial-policy" className="text-secondary hover:underline">Memorial &amp; Obituary Policy</Link> to learn how we care for these records.</p>
        </div>
      </section>
    </div>
  );
}
